/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo, MouseEvent } from "react";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { localPoint } from "@visx/event";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import dayjs from "dayjs";
import { When } from "react-if";
import { theme } from "@nordcloud/gnui";
import { Legend } from "~/components/Charts/components";
import {
  ChartType,
  CostAnalysisFields,
  Granularity,
  Period,
  TopGroupsCostChartProps,
} from "~/components/Charts/CostAnalysis/types";
import {
  getMinMax,
  calculateMinDomain,
  calculateMaxDomain,
  getInnerBarPadding,
  getOuterBarPadding,
} from "~/components/Charts/utils";
import { BrickLoader } from "~/components/Loaders";
import { ChartDataDisplayWrapper } from "~/components/NoData";
import { LoaderWrap } from "~/components/Utils";
import { useCurrency } from "~/hooks";
import { isEmpty, formatMoneyLabel, noop } from "~/tools";
import {
  BarStackRenderer,
  invoiceCorrectionColor,
  StackBarTooltip,
} from "./components";
import { formatChartDates } from "./components/utils";
import {
  getTickDateFormatStackBarChart,
  getTicksNumStackBarChart,
  getXDatesStackBarChart,
  getAllDayValues,
  mapDataWithOtherCosts,
  getNoDataMessage,
  isValueLessThanCent,
} from "./helpers";
import { getTotalValues } from "./utils";

type Props = {
  width: number;
  height: number;
  startDate: string;
  endDate: string;
  loading: boolean;
  granularity: Granularity;
  data: TopGroupsCostChartProps[];
  period: Period;
  showTooltip: (args: TooltipArguments) => void;
  hideTooltip: () => void;
  colors: { [k: string]: string };
  keys: string[];
  showOthers?: boolean;
  chartType?: ChartType;
  containerRef?: (element: HTMLElement | SVGElement | null) => void;
};

const verticalMargin = 50;

export function StackBarChart({
  startDate,
  endDate,
  granularity,
  width,
  height,
  data,
  loading,
  period,
  chartType,
  showTooltip,
  hideTooltip,
  colors,
  keys,
  showOthers = false,
  containerRef,
}: Props) {
  const { currency } = useCurrency();

  const xMax = width - verticalMargin;
  const yMax = height - verticalMargin;

  // Calculate number of ticks on X axis
  const endDateDayjs = dayjs(endDate).endOf(granularity);
  const xTicks = Math.ceil(endDateDayjs.diff(startDate, granularity, true));

  // Prepare array with all dates in range
  const xDates = useMemo(
    () => getXDatesStackBarChart(xTicks, startDate, granularity),
    [startDate, granularity, xTicks]
  );

  const barChartData = mapDataWithOtherCosts(
    data,
    showOthers,
    chartType
  ).filter((item) => xDates.includes(item.date));

  const totalData = getTotalValues(barChartData);

  // Prepare array with all values in range
  const yData = useMemo(
    () => barChartData?.flatMap((item) => getAllDayValues(item)),
    [barChartData]
  );

  const chartData = useMemo(
    () =>
      barChartData?.map((item) => {
        const k = Object.keys(item);
        const diff = arrayDiff(keys, k);

        const rest = Object.fromEntries(diff.map((items) => [items, 0]));

        return {
          ...item,
          ...rest,
        };
      }) ?? [],
    [barChartData, keys]
  );

  const getDate = (item: Item) => xDates.indexOf(item.date);

  const isCostsBelowCent = data.every(({ value }) =>
    isValueLessThanCent(value)
  );

  const hasNoData = useMemo(
    () =>
      isEmpty(data ?? []) ||
      data?.every((timePoint) => Number(timePoint.value) === 0) ||
      isCostsBelowCent,
    [data]
  );

  const hasNegativeValues = useMemo(
    () => yData?.some((value) => Number(value) < 0),
    [yData]
  );

  const xScale = useMemo(
    () =>
      scaleBand<number>({
        range: [1, xMax],
        domain: xDates?.map((_, index) => index),
        padding: 0.1,
      }),
    [xMax, xDates]
  );

  const minMax = getMinMax([...totalData, ...yData]);

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        range: [yMax, 0],
        domain: [
          calculateMinDomain(minMax, hasNoData),
          calculateMaxDomain(minMax, hasNoData),
        ],
        round: true,
        nice: true,
      }),
    [yMax, hasNoData, minMax]
  );

  const ticks = useMemo(
    () => getTicksNumStackBarChart(width, period),
    [width, period]
  );

  const dateRangeDates =
    period === Period.RANGE && granularity === Granularity.MONTHS
      ? formatChartDates(startDate, endDate, period)
      : undefined;

  const showTooltipElement = (event: MouseEvent, index: number) => {
    const coords = localPoint(event, event);

    showTooltip({
      tooltipLeft: coords?.x ?? event.clientX,
      tooltipTop: (coords?.y ?? event.clientY) - window.scrollY,
      tooltipData: (
        <StackBarTooltip
          data={chartData}
          index={index}
          granularity={granularity}
          currency={currency}
          colors={colors}
          dateRangeDates={dateRangeDates}
          chartType={chartType}
        />
      ),
    });
  };

  const tickDateFormat = getTickDateFormatStackBarChart(granularity);

  xScale.paddingInner(getInnerBarPadding(xDates.length, width));
  xScale.paddingOuter(getOuterBarPadding(xDates.length));
  return (
    <LoaderWrap
      loading={loading}
      Component={BrickLoader}
      viewBox="0 0 6 1"
      backgroundColor={theme.color.background.ui01}
    >
      <ChartDataDisplayWrapper
        hasData={!hasNoData}
        messageComponent={getNoDataMessage(isCostsBelowCent, granularity)}
      >
        <svg
          ref={containerRef}
          width="100%"
          height={height}
          css={{ marginTop: theme.spacing.spacing04 }}
          onMouseEnter={hideTooltip}
        >
          <Group top={verticalMargin / 2} left={50}>
            <GridRows
              scale={yScale}
              width={xMax}
              strokeDasharray="1,3"
              stroke={theme.color.border.focus}
              strokeOpacity={1}
              pointerEvents="none"
              numTicks={5}
            />
            <When condition={hasNegativeValues}>
              <GridRows
                scale={yScale}
                width={xMax}
                stroke={theme.color.border.border02}
                strokeOpacity={1}
                pointerEvents="none"
                numTicks={1}
                tickValues={[0]}
              />
            </When>
            <AxisLeft
              top={0}
              scale={yScale}
              stroke={theme.color.border.focus}
              tickStroke="transparent"
              numTicks={6}
              tickFormat={(value) => formatMoneyLabel(value, currency)}
              left={2}
              tickLabelProps={() => ({
                fill: theme.color.text.text02,
                fontSize: 10,
                textAnchor: "end",
              })}
            />
            <AxisBottom
              top={yMax}
              scale={xScale}
              stroke={theme.color.border.focus}
              tickStroke={theme.color.border.focus}
              numTicks={ticks}
              tickFormat={(index) =>
                dayjs(xDates[index]).format(tickDateFormat)
              }
              tickLabelProps={() => ({
                fill: theme.color.text.text02,
                fontSize: 10,
                textAnchor: "middle",
              })}
            />
            <BarStackRenderer
              keys={keys}
              data={chartData ?? []}
              yScale={yScale}
              xScale={xScale}
              x={getDate}
              chartType={chartType}
              colors={colors}
              onMouseLeave={noop}
              onMouseOver={(event, index) => {
                showTooltipElement(event, index);
              }}
            />
          </Group>
        </svg>
        <Legend
          inline
          data={keys.map((key) => ({
            color: getLegendLabelColor(colors, key),
            label: getLegendLabel(key, granularity, chartType),
          }))}
        />
      </ChartDataDisplayWrapper>
    </LoaderWrap>
  );
}

type Item = {
  [key: string]: string;
};

function arrayDiff(arr1: string[], arr2: string[]) {
  return arr1.filter((x) => !arr2.includes(x));
}

type TooltipArguments = {
  tooltipLeft: number;
  tooltipTop: number;
  tooltipData: JSX.Element;
};

function getLegendLabel(
  key: string,
  granularity: Granularity,
  chartType?: ChartType
) {
  if (chartType !== ChartType.ACCUMULATED_COST) {
    return key;
  }

  if (key === CostAnalysisFields.DAILY_COST) {
    return granularity === Granularity.DAYS
      ? key
      : CostAnalysisFields.MONTHLY_COST;
  }

  return key;
}

function getLegendLabelColor(
  colors: {
    [k: string]: string;
  },
  key: string
) {
  if (key === CostAnalysisFields.CORRECTION_COST) {
    return invoiceCorrectionColor;
  }

  return colors[key];
}
